import { useTranslation } from 'react-i18next';
import { faUserPlus, faUserEdit, faUserMinus, faUserGroup } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeleteModal from '@shared/components/DeleteModal';
import UsersForm from './UsersForm';
import { useUsersTable } from '@modules/users/hooks/useUsersTable';
import UserGroupsList from '@modules/userGroups/componenets/UserGroupsList';
import Table from '@shared/components/Table';

const UsersTable = ({ isSidebarExpanded }) => {
    const { t } = useTranslation();
    const {
        data,
        loading,
        error,
        getData,
        selectedData,
        modalState,
        pageNumber,
        pageSize,
        sortDir,
        totalElements,
        totalPages,
        searchQuery,
        searchType,
        searchOptions,
        handleSaveGroup,
        handleOpenModal,
        handleCloseModal,
        handleAddCloseModal,
        handleEditUser,
        handleConfirmDelete,
        handleSearchClick,
        handleCheckboxChange,
        handleSort,
        setSearchQuery,
        setSearchType,
        setPageSize,
        setpageNumber,
        selectedUserName,
        fetchData
    } = useUsersTable();

    const columns = [
        { key: 'username', label: t('users.table.headers.username'), sortable: true },
        { key: 'personFullName', label: t('users.table.headers.accountname'), sortable: true },
        { key: 'description', label: t('users.table.headers.description'), sortable: false },
        { key: 'onlyWebAccess', label: t('users.table.headers.webAccess'), sortable: false },
        { key: 'ldapAuth', label: t('users.table.headers.ldapAccess'), sortable: false },
        { key: 'language', label: t('users.table.headers.domain'), sortable: false },
        { key: 'isLocked', label: t('users.table.headers.locked'), sortable: false }
    ];

    const actionButtons = (
        <>
            <button
                className="icon-button"
                onClick={() => handleOpenModal('add')}
                title={t('users.table.actions.add')}
            >
                <FontAwesomeIcon icon={faUserPlus} />
            </button>
            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={() => handleEditUser()}
                title={t('users.table.actions.edit')}
            >
                <FontAwesomeIcon icon={faUserEdit} />
            </button>
            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={() => handleOpenModal('delete')}
                title={t('users.table.actions.delete')}
            >
                <FontAwesomeIcon icon={faUserMinus} />
            </button>
            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={() => handleOpenModal('group')}
                title={t('users.table.actions.group')}
            >
                <FontAwesomeIcon icon={faUserGroup} />
            </button>
        </>
    );

    return (
        <>
            <Table
                // Data and state
                data={data}
                loading={loading}
                error={error}
                selectedData={selectedData}
                pageNumber={pageNumber}
                pageSize={pageSize}
                sortBy="username"
                sortDir={sortDir}
                totalElements={totalElements}
                totalPages={totalPages}
                searchQuery={searchQuery}
                searchType={searchType}
                searchOptions={searchOptions}

                // Column configuration
                columns={columns}

                // Title and actions
                title={t('users.title')}
                actionButtons={actionButtons}

                // Handlers
                handleSort={handleSort}
                handleCheckboxChange={handleCheckboxChange}
                handleSearchClick={handleSearchClick}
                setSearchQuery={setSearchQuery}
                setSearchType={setSearchType}
                setPageSize={setPageSize}
                setpageNumber={setpageNumber}
                getData={getData}

                // Custom renderers
                renderCell={(item, column) => {
                    if (column.key === 'onlyWebAccess') {
                        return (
                            <span className={`status-badge ${item.onlyWebAccess ? 'active' : 'inactive'}`}>
                                {item.onlyWebAccess ? 'Yes' : 'No'}
                            </span>
                        );
                    }
                    if (column.key === 'ldapAuth') {
                        return (
                            <span className={`status-badge ${item.ldapAuth ? 'active' : 'inactive'}`}>
                                {item.ldapAuth ? 'Yes' : 'No'}
                            </span>
                        );
                    }
                    if (column.key === 'isLocked') {
                        return (
                            <span className={`status-badge ${item.isLocked === 'y' ? 'locked' : 'active'}`}>
                                {item.isLocked === 'y' ? 'Yes' : 'No'}
                            </span>
                        );
                    }
                    return item[column.key];
                }}
            />

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    selectedData={selectedData}
                    name={selectedUserName}
                />
            )}

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <UsersForm
                    onClose={handleAddCloseModal}
                    userId={modalState.type === 'edit' ? selectedData : null}
                    selectedData={selectedData}
                    fetchData={fetchData}
                />
            )}

            {modalState.visible && modalState.type === 'group' && (
                <UserGroupsList
                    onClose={handleCloseModal}
                    onSave={handleSaveGroup}
                    groupData={selectedData}
                />
            )}
        </>
    );
};

export default UsersTable;